import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  LabelList, Cell, Legend,
} from 'recharts';
import './App.css';
import './AnalyticsPage.css';
import Navigation from './Navigation';
import { usePageTracking } from './hooks/useAnalytics';

const COLORS = {
  ink: '#1C201E',
  cream: '#FAF7F0',
  muted: '#6B7F75',
  forest: '#2F4B3C',
  bronze: '#917841',
  bronzeSoft: 'rgba(145, 120, 65, 0.15)',
};

const ROW_HEIGHT = 28;  // px per y-axis row — keep readable and ensure labels fit

function pct(n, digits = 1) {
  return `${(n * 100).toFixed(digits)}%`;
}

function ChartCard({ title, description, children, height = 360, toggle = null, raw = false, fullWidth = false }) {
  return (
    <section className={`analytics-card${fullWidth ? ' analytics-card--full' : ''}`}>
      <div className="analytics-card-header">
        <div>
          <h2>{title}</h2>
          {description && <p className="analytics-card-desc">{description}</p>}
        </div>
        {toggle}
      </div>
      <div className="analytics-chart" style={raw ? undefined : { height }}>
        {raw ? children : (
          <ResponsiveContainer width="100%" height="100%">
            {children}
          </ResponsiveContainer>
        )}
      </div>
    </section>
  );
}

function ViewToggle({ value, onChange }) {
  return (
    <div className="analytics-toggle" role="tablist">
      <button
        type="button"
        role="tab"
        aria-selected={value === 'brand'}
        className={value === 'brand' ? 'active' : ''}
        onClick={() => onChange('brand')}
      >
        By brand
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={value === 'model'}
        className={value === 'model' ? 'active' : ''}
        onClick={() => onChange('model')}
      >
        By model
      </button>
    </div>
  );
}

// Decreasing opacities for stacked segment layers m0..m4, mOther.
const SEGMENT_OPACITIES = [1.0, 0.82, 0.65, 0.50, 0.37, 0.22];
const SEGMENT_KEYS = ['m0', 'm1', 'm2', 'm3', 'm4', 'mOther'];

function stackedToRows(stacked) {
  return stacked.map(brand => {
    const row = { name: brand.name, totalShare: brand.totalShare };
    for (const seg of brand.segments) {
      row[seg.key] = seg.share;
      row[`${seg.key}_label`] = seg.label;
      row[`${seg.key}_ofBrand`] = seg.shareOfBrand;
    }
    return row;
  });
}

function stripBrandPrefix(label, brand) {
  if (!label) return label;
  if (label === `Other ${brand}`) return 'Other';
  if (label.startsWith(`${brand} `)) return label.slice(brand.length + 1);
  return label;
}

function StackedTooltip({ active, payload, hoverKey }) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload;
  const items = payload
    .filter(p => (p.value ?? 0) > 0)
    .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
  if (items.length === 0) return null;
  return (
    <div className="stacked-tooltip">
      <div className="stacked-tooltip-title">{row.name}</div>
      <div className="stacked-tooltip-rows">
        {items.map(item => {
          const k = item.dataKey;
          const label = stripBrandPrefix(row[`${k}_label`] || k, row.name);
          const ofBrand = row[`${k}_ofBrand`];
          const isActive = hoverKey === k;
          return (
            <div
              key={k}
              className={`stacked-tooltip-row${isActive ? ' is-active' : ''}`}
            >
              <span className="stacked-tooltip-label">{label}</span>
              <span className="stacked-tooltip-value">
                {pct(ofBrand ?? item.value, 1)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StackedBrandModelChart({ data: stacked }) {
  const data = stackedToRows(stacked);
  const [hoverKey, setHoverKey] = useState(null);
  return (
    <BarChart data={data} layout="vertical" margin={{ top: 4, right: 56, bottom: 4, left: 8 }}>
      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={COLORS.bronzeSoft} />
      <XAxis type="number" tickFormatter={v => `${Math.round(v * 100)}%`} stroke={COLORS.muted} />
      <YAxis
        type="category"
        dataKey="name"
        width={130}
        stroke={COLORS.muted}
        tick={{ fontSize: 12 }}
        interval={0}
      />
      <Tooltip
        cursor={{ fill: 'rgba(145, 120, 65, 0.06)' }}
        content={props => <StackedTooltip {...props} hoverKey={hoverKey} />}
      />
      {SEGMENT_KEYS.map((key, i) => (
        <Bar
          key={key}
          dataKey={key}
          stackId="share"
          fill={COLORS.forest}
          fillOpacity={SEGMENT_OPACITIES[i]}
          activeBar={{
            fill: COLORS.forest,
            fillOpacity: Math.min(1, SEGMENT_OPACITIES[i] + 0.18),
            stroke: COLORS.ink,
            strokeWidth: 1,
          }}
          onMouseEnter={() => setHoverKey(key)}
          onMouseLeave={() => setHoverKey(null)}
          isAnimationActive={true}
        />
      ))}
    </BarChart>
  );
}

function ShareBarChart({ data, yAxisWidth = 130, colorFn = null }) {
  return (
    <BarChart data={data} layout="vertical" margin={{ top: 4, right: 56, bottom: 4, left: 8 }}>
      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={COLORS.bronzeSoft} />
      <XAxis type="number" tickFormatter={v => `${Math.round(v * 100)}%`} stroke={COLORS.muted} />
      <YAxis
        type="category"
        dataKey="name"
        width={yAxisWidth}
        stroke={COLORS.muted}
        tick={{ fontSize: 12 }}
        interval={0}
      />
      <Tooltip formatter={v => pct(v, 1)} />
      <Bar dataKey="share" radius={[0, 4, 4, 0]} isAnimationActive={true}>
        {colorFn
          ? data.map((d, i) => <Cell key={i} fill={colorFn(d, i).fill} fillOpacity={colorFn(d, i).opacity} />)
          : data.map((d, i) => <Cell key={i} fill={COLORS.forest} />)}
        <LabelList dataKey="share" position="right" formatter={v => pct(v, 1)} style={{ fill: COLORS.ink, fontSize: 11 }} />
      </Bar>
    </BarChart>
  );
}

// Distinct base color per brand + opacity by rank within brand. Brand → color is
// assigned by first appearance in the data (so the most-shared brand gets color 0).
// Models from the same brand share a color family but differ in opacity.
function makeBrandColorFn(rows) {
  const BRAND_PALETTE = [
    '#2F4B3C', '#917841', '#3E6781', '#B5623E', '#5E7A5C',
    '#8B4A6B', '#C89B3C', '#4A7856', '#A65E2B', '#6B7F75',
  ];
  const OPACITIES = [1.0, 0.82, 0.65, 0.50, 0.37, 0.22];
  const brandToColor = new Map();
  for (const r of rows) {
    if (r.brand && !brandToColor.has(r.brand)) {
      const idx = brandToColor.size;
      brandToColor.set(r.brand, BRAND_PALETTE[idx % BRAND_PALETTE.length]);
    }
  }
  return (d) => ({
    fill: brandToColor.get(d.brand) || COLORS.muted,
    opacity: OPACITIES[Math.min(d.rankInBrand ?? 0, OPACITIES.length - 1)],
  });
}

// --- Custom violin + extent chart for accuracy ---

const MODE_COLORS = {
  quicktap: '#6B7F75',
  photo: '#2F4B3C',
  audio: '#917841',
};
const MODE_LABELS = {
  quicktap: 'Quick-tap',
  photo: 'Photo',
  audio: 'Audio',
};

function mergeHistograms(hists) {
  const acc = new Map();
  for (const arr of hists) {
    for (const { bin, count } of arr || []) acc.set(bin, (acc.get(bin) || 0) + count);
  }
  return Array.from(acc, ([bin, count]) => ({ bin, count })).sort((a, b) => a.bin - b.bin);
}

function prepareAccuracyRows(brands, modeFilter) {
  return brands
    .map(b => {
      let histogram;
      let medianForSort;
      if (modeFilter === 'all') {
        histogram = mergeHistograms([b.histograms.quicktap, b.histograms.photo, b.histograms.audio]);
        // Sort by the best-populated mode's median
        const best = ['quicktap', 'photo', 'audio']
          .map(m => b.modes[m])
          .filter(Boolean)
          .sort((x, y) => y.n - x.n)[0];
        medianForSort = best ? Math.abs(best.p50) : Number.POSITIVE_INFINITY;
      } else {
        histogram = b.histograms[modeFilter] || [];
        const m = b.modes[modeFilter];
        medianForSort = m ? Math.abs(m.p50) : Number.POSITIVE_INFINITY;
      }
      return { ...b, histogram, medianForSort };
    })
    .filter(b => modeFilter === 'all' || b.modes[modeFilter])
    .sort((a, b) => a.medianForSort - b.medianForSort);
}

function ModeSelector({ value, onChange, available }) {
  const options = [
    { key: 'all', label: 'All' },
    { key: 'quicktap', label: 'Quick-tap' },
    { key: 'photo', label: 'Photo' },
    { key: 'audio', label: 'Audio' },
  ].filter(o => o.key === 'all' || available.has(o.key));
  return (
    <div className="analytics-mode-selector" role="tablist">
      {options.map(o => (
        <button
          key={o.key}
          type="button"
          role="tab"
          aria-selected={value === o.key}
          className={value === o.key ? 'active' : ''}
          onClick={() => onChange(o.key)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function AccuracyViolinChart({ brands: rawBrands, modeFilter }) {
  const brands = prepareAccuracyRows(rawBrands, modeFilter);
  // Fixed layout
  const LABEL_W = 120;
  const PAD_L = LABEL_W + 8;
  const PAD_R = 16;
  const PAD_T = 24;
  const PAD_B = 48;
  const ROW_H = 30;
  const VIOLIN_MAX_H = 22;  // max vertical extent of violin shape per row
  const X_MIN = -20;  // s/day
  const X_MAX = 20;
  const totalWidth = 860;
  const totalHeight = PAD_T + brands.length * ROW_H + PAD_B;

  const xScale = (v) => PAD_L + ((Math.max(X_MIN, Math.min(X_MAX, v)) - X_MIN) / (X_MAX - X_MIN)) * (totalWidth - PAD_L - PAD_R);
  const binToSpd = (bin) => bin / 2;  // SQL bin represents floor(spd * 2)

  const [hoverIdx, setHoverIdx] = useState(null);

  return (
    <div className="accuracy-violin-container">
      <svg width="100%" viewBox={`0 0 ${totalWidth} ${totalHeight}`} role="img" aria-label="Accuracy distribution by brand">
        {/* X-axis gridlines and labels */}
        {[-20, -10, -5, 0, 5, 10, 20].map(v => (
          <g key={v}>
            <line x1={xScale(v)} x2={xScale(v)} y1={PAD_T} y2={totalHeight - PAD_B}
              stroke={COLORS.bronzeSoft} strokeDasharray="3 3" />
            <text x={xScale(v)} y={totalHeight - PAD_B + 18} textAnchor="middle"
              fontSize="11" fill={COLORS.muted}>{v > 0 ? `+${v}` : v}</text>
          </g>
        ))}
        {/* Zero line, slightly stronger */}
        <line x1={xScale(0)} x2={xScale(0)} y1={PAD_T} y2={totalHeight - PAD_B}
          stroke={COLORS.muted} strokeWidth={0.7} opacity={0.5} />
        <text x={(PAD_L + totalWidth - PAD_R) / 2} y={totalHeight - 4} textAnchor="middle"
          fontSize="11" fill={COLORS.muted}>seconds per day (drift rate)</text>

        {brands.map((brand, i) => {
          const rowY = PAD_T + i * ROW_H + ROW_H / 2;
          const maxCount = Math.max(1, ...brand.histogram.map(h => h.count));
          // Build violin path by walking histogram left-to-right and mirroring
          const violinTop = brand.histogram.map(h => ({
            x: xScale(binToSpd(h.bin) + 0.25),  // center of bin
            h: (h.count / maxCount) * (VIOLIN_MAX_H / 2) * 0.95,
          }));
          let d = '';
          if (violinTop.length > 0) {
            d += `M ${violinTop[0].x} ${rowY}`;
            for (const p of violinTop) d += ` L ${p.x} ${rowY - p.h}`;
            for (let j = violinTop.length - 1; j >= 0; j--) d += ` L ${violinTop[j].x} ${rowY + violinTop[j].h}`;
            d += ' Z';
          }

          const isHovered = hoverIdx === i;

          return (
            <g key={brand.name}
              onMouseEnter={() => setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx(null)}
            >
              {/* Hover strip for easier targeting */}
              <rect x={0} y={rowY - ROW_H / 2} width={totalWidth} height={ROW_H}
                fill={isHovered ? 'rgba(145,120,65,0.06)' : 'transparent'} />

              {/* Brand label */}
              <text x={LABEL_W} y={rowY + 4} textAnchor="end" fontSize="12"
                fill={COLORS.ink} fontWeight={isHovered ? 600 : 400}>{brand.name}</text>

              {/* Violin shape */}
              {d && <path d={d} fill={COLORS.forest} fillOpacity={isHovered ? 0.6 : 0.38} />}

              {/* Per-mode extent lines (p10 → p90), stacked above/below the violin midline.
                  When a specific mode is selected, only that mode's line renders; when 'all',
                  show all three faintly. */}
              {['quicktap', 'photo', 'audio'].map((mode, mi) => {
                const m = brand.modes[mode];
                if (!m) return null;
                if (modeFilter !== 'all' && modeFilter !== mode) return null;
                const yOffset = modeFilter === 'all' ? (mi - 1) * 4 : 0;
                return (
                  <g key={mode}>
                    <line
                      x1={xScale(m.p10)} x2={xScale(m.p90)}
                      y1={rowY + yOffset} y2={rowY + yOffset}
                      stroke={MODE_COLORS[mode]}
                      strokeWidth={1.5}
                      opacity={isHovered ? 0.9 : 0.35}
                    />
                    <circle
                      cx={xScale(m.p50)} cy={rowY + yOffset} r={2}
                      fill={MODE_COLORS[mode]} opacity={isHovered ? 0.95 : 0.5}
                    />
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>

      {hoverIdx !== null && brands[hoverIdx] && (
        <div className="accuracy-violin-tooltip">
          <strong>{brands[hoverIdx].name}</strong>
          <div className="accuracy-violin-tt-modes">
            {['quicktap', 'photo', 'audio'].map(mode => {
              const m = brands[hoverIdx].modes[mode];
              if (!m) return null;
              return (
                <div key={mode} className="accuracy-violin-tt-mode">
                  <span className="accuracy-violin-tt-swatch" style={{ background: MODE_COLORS[mode] }} />
                  <span className="accuracy-violin-tt-name">{MODE_LABELS[mode]}</span>
                  <span className="accuracy-violin-tt-stats">
                    median {m.p50.toFixed(2)} s/day · middle 50% {m.p25.toFixed(1)} to {m.p75.toFixed(1)} · n={m.n}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const BRAND_LINE_COLORS = [
  '#2F4B3C',  // forest
  '#917841',  // bronze
  '#3E6781',  // slate blue
  '#B5623E',  // terracotta
  '#5E7A5C',  // sage
  '#8B4A6B',  // plum
  '#C89B3C',  // gold
  '#6B7F75',  // muted
  '#4A7856',  // moss
  '#A65E2B',  // rust
];

function GrowthByBrandChart({ data, brands }) {
  return (
    <LineChart data={data} margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
      <CartesianGrid strokeDasharray="3 3" stroke={COLORS.bronzeSoft} />
      <XAxis dataKey="week" stroke={COLORS.muted} tick={{ fontSize: 11 }} />
      <YAxis
        stroke={COLORS.muted}
        tick={{ fontSize: 11 }}
        domain={[
          (dataMin) => Math.max(0, Math.floor(dataMin - 2)),
          (dataMax) => Math.ceil(dataMax + 2),
        ]}
        allowDecimals={false}
        label={{ value: "Cumulative watches (first week = 100)", angle: -90, position: 'insideLeft', style: { fill: COLORS.muted, fontSize: 11 } }}
      />
      <Tooltip
        formatter={v => (v == null ? '—' : `${v}`)}
        itemSorter={item => -(item.value ?? -Infinity)}
      />
      <Legend verticalAlign="top" height={48} wrapperStyle={{ fontSize: 12, paddingBottom: 8 }} />
      {brands.map((brand, i) => (
        <Line
          key={brand}
          type="monotone"
          dataKey={brand}
          stroke={BRAND_LINE_COLORS[i % BRAND_LINE_COLORS.length]}
          strokeWidth={2}
          dot={false}
          connectNulls={false}
          name={brand}
        />
      ))}
    </LineChart>
  );
}

function useIsMobile(maxPx = 640) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(`(max-width: ${maxPx}px)`).matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(`(max-width: ${maxPx}px)`);
    const h = e => setIsMobile(e.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, [maxPx]);
  return isMobile;
}

function AnalyticsPage() {
  usePageTracking('analytics', { page_title: 'ChronoLog Analytics' });
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [collectedView, setCollectedView] = useState('brand');
  const [measuredView, setMeasuredView] = useState('brand');
  const [accuracyMode, setAccuracyMode] = useState('all');
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const isMobile = useIsMobile();
  const modelYAxisWidth = isMobile ? 120 : 180;

  useEffect(() => {
    fetch('/api/analytics')
      .then(r => r.ok ? r.json() : r.text().then(t => Promise.reject(t)))
      .then(setData)
      .catch(err => setError(typeof err === 'string' ? err : err.message));
  }, []);

  const barHeight = (rows) => Math.max(300, rows.length * ROW_HEIGHT + 40);

  return (
    <div className="analytics-page">
      <Helmet>
        <title>ChronoLog Analytics — Collector Insights</title>
        <meta name="description" content="Aggregate insights from the ChronoLog community: brand popularity, accuracy by brand, and timing trends from real collectors." />
        <link rel="canonical" href="https://www.chronolog.watch/analytics" />
      </Helmet>

      <Navigation />

      <main className="analytics-main">
        <header className="analytics-hero">
          <div className="analytics-hero-row">
            <h1>ChronoLog Analytics</h1>
            <button
              type="button"
              className="analytics-hero-info"
              onClick={() => setHowItWorksOpen(true)}
              aria-label="How ChronoLog works"
            >
              How ChronoLog works
            </button>
          </div>
          <p className="analytics-hero-note">
            Aggregated shares and averages — no individual collections or users are identified.
          </p>
        </header>

        {howItWorksOpen && (
          <div className="modal-overlay" onClick={() => setHowItWorksOpen(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>How ChronoLog works</h3>
                <button
                  className="modal-close"
                  onClick={() => setHowItWorksOpen(false)}
                  aria-label="Close"
                >×</button>
              </div>
              <div className="modal-body">
                <p>
                  ChronoLog turns the phone already in your pocket into a timing
                  companion for your watch collection.
                </p>
                <p>
                  <strong>Your collection.</strong> Add each watch with its brand,
                  model, and movement so ChronoLog can track how it runs over time.
                </p>
                <p>
                  <strong>Offset measurements.</strong> Capture where your watch
                  stands right now — either by photographing the dial and entering
                  the time it shows, or by picking a target time and tapping when
                  the watch reaches it. ChronoLog compares that reading against
                  atomic time and, as you log more, calculates drift per day.
                </p>
                <p>
                  <strong>Audio measurements.</strong> Hold the phone close to the
                  watch and ChronoLog's audio processing pipeline listens to the
                  escapement, returning an instant rate reading — no 24-hour wait.{' '}
                  <a href="/timegrapher">Read how the audio pipeline works →</a>
                </p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="analytics-error">
            Couldn't load analytics: {error}
          </div>
        )}

        {!data && !error && <div className="analytics-loading">Loading analytics…</div>}

        {data && (
          <div className="analytics-grid">
            <ChartCard
              title="Most collected"
              description={
                collectedView === 'brand'
                  ? 'Share of watches tracked in ChronoLog by brand. Each bar is segmented by model — darker shades are the top few models within that brand.'
                  : 'Share of watches tracked, broken out by brand + model.'
              }
              height={
                collectedView === 'brand'
                  ? barHeight(data.collectedStacked)
                  : barHeight(data.modelShare)
              }
              toggle={<ViewToggle value={collectedView} onChange={setCollectedView} />}
            >
              {collectedView === 'brand'
                ? <StackedBrandModelChart data={data.collectedStacked} />
                : <ShareBarChart data={data.modelShare} yAxisWidth={modelYAxisWidth} colorFn={makeBrandColorFn(data.modelShare)} />}
            </ChartCard>

            <ChartCard
              title="Most measured"
              description={
                measuredView === 'brand'
                  ? 'Share of timing measurements taken by brand. Each bar is segmented by model — darker shades are the top few models within that brand.'
                  : 'Share of timing measurements, broken out by brand + model.'
              }
              height={
                measuredView === 'brand'
                  ? barHeight(data.measuredStacked)
                  : barHeight(data.measurementModelShare)
              }
              toggle={<ViewToggle value={measuredView} onChange={setMeasuredView} />}
            >
              {measuredView === 'brand'
                ? <StackedBrandModelChart data={data.measuredStacked} />
                : <ShareBarChart data={data.measurementModelShare} yAxisWidth={modelYAxisWidth} colorFn={makeBrandColorFn(data.measurementModelShare)} />}
            </ChartCard>

            {data.accuracyBrands && data.accuracyBrands.length > 0 && (() => {
              const available = new Set();
              for (const b of data.accuracyBrands) {
                for (const m of ['quicktap', 'photo', 'audio']) {
                  if (b.modes[m]) available.add(m);
                }
              }
              return (
                <ChartCard
                  title="Accuracy by brand"
                  description="Distribution of seconds-per-day drift. The violin shape shows where measurements cluster; thin overlaid lines show each mode's middle-80% range with a dot at the median. Hover a row for details. Brands are sorted by median — most accurate at top."
                  height={24 + data.accuracyBrands.length * 30 + 40}
                  raw
                  fullWidth
                  toggle={
                    <ModeSelector
                      value={accuracyMode}
                      onChange={setAccuracyMode}
                      available={available}
                    />
                  }
                >
                  <AccuracyViolinChart brands={data.accuracyBrands} modeFilter={accuracyMode} />
                </ChartCard>
              );
            })()}

            {data.growthByBrand && data.growthByBrand.length > 0 && (
              <ChartCard
                title="Growth of top brands"
                description={`Cumulative active watches per brand over the last 3 months for the top ${data.growthBrandsShown.length} brands. Each brand is indexed to its first week in view = 100, so the lines show relative growth regardless of starting scale.`}
                height={440}
                fullWidth
              >
                <GrowthByBrandChart data={data.growthByBrand} brands={data.growthBrandsShown} />
              </ChartCard>
            )}

            <footer className="analytics-footer">
              Last updated: {new Date(data.generatedAt).toLocaleDateString()}
            </footer>
          </div>
        )}
      </main>
    </div>
  );
}

export default AnalyticsPage;
